/**
 * DataGridToolbar - Standalone toolbar component for V2 DataGrids.
 *
 * Uses omnisearch hooks internally (no prop drilling for omnisearch state).
 * Handles manage columns popover internally.
 *
 * For standard usage, prefer OmnisearchDataGridV2 which combines this toolbar
 * with SuspenseDataGrid. Use DataGridToolbar directly for custom layouts.
 */

import FilterList from "@mui/icons-material/FilterList";
import { Box, Stack, SxProps, Theme, useTheme } from "@mui/material";
import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-premium";
import { CommandButton } from "components/CommandButton";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { Omnisearch } from "components/Omnisearch/Omnisearch";
import { OmnisearchPillRow } from "components/Omnisearch/OmnisearchPillRow";
import { OmnisearchColumnPopover } from "components/OmnisearchColumnPopover";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { MouseEvent, useCallback, useMemo, useState } from "react";

import { useOmnisearchActions, useOmnisearchStore } from "./omnisearchStore";

// =============================================================================
// Types
// =============================================================================

/**
 * The omnisearch state a toolbar/grid needs: the current string and the two
 * setters. The URL store supplies these across `useOmnisearchStore` +
 * `useOmnisearchActions`; `useLocalOmnisearch` returns all three on one object,
 * so its result already satisfies this without any adapter.
 */
export interface OmnisearchSource {
  omnisearch: string;
  setOmnisearch: (value: string) => void;
  setOmnisearchImmediate: (value: string) => void;
}

export interface DataGridToolbarProps {
  // ─────────────────────────────────────────────────────────────────────────
  // Filter configuration (should be module-level constants for stability)
  // ─────────────────────────────────────────────────────────────────────────
  /** Filter keys derived from columns + additional keys */
  columnFilterKeys?: FilterKey[];
  /** Filter groups for popover organization */
  filterGroups?: FilterGroup[];
  /** Key aliases for filter mapping (e.g., { tags: "facilityTags" }) */
  keyAliases?: Record<string, string>;
  /**
   * Omnisearch keys that are valid in saved filters but are not column filter
   * keys — e.g. page-level toggles like the fulfillment "View by" selector.
   * Without this they'd be stripped by filterValidKeys when a saved filter is
   * restored. Pass a stable (module-level) array reference to avoid re-renders.
   */
  preservedFilterKeys?: string[];
  /** Tenant ID for backend queries */
  tenantId?: string;
  /** Persistence key for saved filters in localStorage */
  persistenceKey?: string;
  /** Show external filter button. Default: true */
  showFilters?: boolean;
  /** Hide omnisearch input entirely. Default: false */
  hideOmnisearch?: boolean;
  /**
   * Shorten the search input and tighten the gap between this row and the grid
   * below it. Default: false.
   *
   * For a grid embedded in a section card that already supplies its own
   * padding, where the standalone page's generous spacing reads as a hole
   * between the card's heading and the search box. Opt-in so every existing
   * grid keeps the spacing it was designed with.
   */
  compact?: boolean;
  /**
   * Where this toolbar's search state lives. Omit it and the toolbar reads and
   * writes the URL, which is what every page-level grid wants. Pass a
   * `useLocalOmnisearch()` result for a grid inside a drawer or modal, so it
   * neither inherits the page's search term nor overwrites it while the reader
   * types.
   */
  omnisearchSource?: OmnisearchSource;

  // ─────────────────────────────────────────────────────────────────────────
  // Manage Columns (from parent's useGridPersistence - use useMemo/useCallback)
  // ─────────────────────────────────────────────────────────────────────────
  /** Columns for the manage columns popover */
  columns?: GridColDef[];
  /** Current column visibility model */
  columnVisibilityModel?: GridColumnVisibilityModel;
  /** Callback when column visibility changes */
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  /** Show manage columns button. Auto-calculates if undefined (>= 5 non-action columns) */
  shouldShowManageColumns?: boolean;

  // ─────────────────────────────────────────────────────────────────────────
  // Command Buttons (use useMemo for stability)
  // ─────────────────────────────────────────────────────────────────────────
  /** Buttons to render on the right side of the toolbar */
  commandButtons?: React.ReactNode[];
  /** Buttons to render on the left side (before omnisearch) */
  startCommandButtons?: React.ReactNode[];

  // ─────────────────────────────────────────────────────────────────────────
  // Styling
  // ─────────────────────────────────────────────────────────────────────────
  /** Custom styles for the toolbar container */
  sx?: SxProps<Theme>;
}

// =============================================================================
// Component
// =============================================================================

export function DataGridToolbar({
  columnFilterKeys,
  filterGroups,
  keyAliases,
  preservedFilterKeys,
  tenantId,
  persistenceKey,
  showFilters = true,
  hideOmnisearch = false,
  compact = false,
  omnisearchSource,
  columns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  shouldShowManageColumns: shouldShowManageColumnsProp,
  commandButtons,
  startCommandButtons,
  sx,
}: DataGridToolbarProps) {
  const theme = useTheme();

  // ─────────────────────────────────────────────────────────────────────────
  // Internal omnisearch state (uses hooks directly - no prop drilling)
  //
  // Both URL hooks are called on every render so the hook order never depends
  // on whether a caller passed a source. With no source the values below are
  // exactly what they were before the prop existed.
  // ─────────────────────────────────────────────────────────────────────────
  const urlState = useOmnisearchStore();
  const urlActions = useOmnisearchActions();
  const omnisearch = omnisearchSource?.omnisearch ?? urlState.omnisearch;
  const setOmnisearch = omnisearchSource?.setOmnisearch ?? urlActions.setOmnisearch;
  const setOmnisearchImmediate =
    omnisearchSource?.setOmnisearchImmediate ?? urlActions.setOmnisearchImmediate;

  // ─────────────────────────────────────────────────────────────────────────
  // Manage Columns Popover State (internal)
  // ─────────────────────────────────────────────────────────────────────────
  const [columnListAnchorEl, setColumnListAnchorEl] = useState<HTMLButtonElement | null>(null);
  const showColumnList = Boolean(columnListAnchorEl);

  const handleColumnListOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setColumnListAnchorEl(event.currentTarget);
  }, []);

  const handleColumnListClose = useCallback(() => {
    setColumnListAnchorEl(null);
  }, []);

  const handleToggleColumn = useCallback(
    (field: string) => {
      if (!columnVisibilityModel || !onColumnVisibilityModelChange) return;
      const newModel = {
        ...columnVisibilityModel,
        [field]: field in columnVisibilityModel ? !columnVisibilityModel[field] : false,
      };
      onColumnVisibilityModelChange(newModel);
    },
    [columnVisibilityModel, onColumnVisibilityModelChange]
  );

  // Auto-calculate shouldShowManageColumns if not provided
  const shouldShowManageColumns = useMemo(() => {
    if (shouldShowManageColumnsProp !== undefined) {
      return shouldShowManageColumnsProp;
    }
    if (!columns) return false;
    return columns.filter((c) => c.type !== "actions").length >= 5;
  }, [shouldShowManageColumnsProp, columns]);

  // Filter out actions column for the manage columns popover
  const manageableColumns = useMemo(
    () => columns?.filter((col) => col.headerName !== "actions" && col.field !== "actions") ?? [],
    [columns]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Filter Button Render (stable reference)
  // ─────────────────────────────────────────────────────────────────────────
  const renderFilterButton = useCallback(
    (onClick: (event: MouseEvent<HTMLButtonElement>) => void) => (
      <CommandButton onClick={onClick}>
        <FilterList sx={{ mr: 1 }} fontSize="small" />
        Filters
      </CommandButton>
    ),
    []
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={sx}>
      <Stack
        sx={{
          marginBottom: theme.spacing(compact ? 1.5 : 3),
          gap: theme.spacing(1.5),
        }}
        direction="row"
        flexWrap="wrap"
        alignItems="center"
      >
        {/* Left section: startCommandButtons + Omnisearch */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            width: { xs: "100%", lg: "inherit" },
            maxWidth: "1022px",
            flexGrow: 1,
          }}
          gap={theme.spacing(1.5)}
        >
          {startCommandButtons}
          {!hideOmnisearch && (
            <Omnisearch
              // The default input is MUI's `medium` (~56px). In a card the
              // full-height field dominates the row, so compact drops it to
              // `small` (~40px) — MUI's only shorter option.
              size={compact ? "small" : undefined}
              omnisearch={omnisearch}
              setOmnisearch={setOmnisearch}
              setOmnisearchImmediate={setOmnisearchImmediate}
              columnFilterKeys={columnFilterKeys}
              filterGroups={filterGroups}
              keyAliases={keyAliases}
              preservedFilterKeys={preservedFilterKeys}
              showFilterButtonInside={false}
              showExternalFilterButton={showFilters}
              renderFilterButton={renderFilterButton}
              tenantId={tenantId}
              persistenceKey={persistenceKey}
            />
          )}
        </Stack>

        {/* Right section: Manage Columns + commandButtons */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            gap: theme.spacing(1.5),
            flexWrap: "wrap",
            // Below the pill row once the toolbar wraps, so the pills stay
            // visually attached to the search box they belong to. On one line
            // (lg and up) the pills already wrap underneath everything.
            order: { xs: 1, lg: 0 },
          }}
        >
          {shouldShowManageColumns &&
            columns &&
            columnVisibilityModel &&
            onColumnVisibilityModelChange && (
              <>
                <CommandButton onClick={handleColumnListOpen}>Manage Columns</CommandButton>
                {showColumnList && (
                  <OmnisearchColumnPopover
                    showColumnList={showColumnList}
                    anchorEl={columnListAnchorEl}
                    handleCloseColumnList={handleColumnListClose}
                    columns={manageableColumns}
                    handleToggleColumn={handleToggleColumn}
                    selectedColumns={columnVisibilityModel}
                    setSelectedColumns={onColumnVisibilityModelChange}
                  />
                )}
              </>
            )}
          {commandButtons}
        </Stack>

        {/* Applied-filter pills, on their own line below the toolbar. Renders
            nothing unless the preview is on and something is filtered. */}
        <OmnisearchPillRow
          omnisearch={omnisearch}
          onChange={setOmnisearchImmediate}
          onApply={setOmnisearch}
          columnFilterKeys={columnFilterKeys}
          keyAliases={keyAliases}
          preservedFilterKeys={preservedFilterKeys}
          tenantId={tenantId}
        />
      </Stack>
    </Box>
  );
}
